<script setup lang="ts">
const { t } = useI18n();

const {
  title,
  message,
  cta,
  cta2,
  ctaHref,
  ctaArrow = false,
  variant = 'default',
  icon = 'lock',
  height,
} = defineProps<{
  title: string;
  message: string;
  cta?: string;
  cta2?: string;
  ctaHref?: string;
  /** Renders a trailing arrow after `cta` (RTL-flipping). Always shown for the default CTA. */
  ctaArrow?: boolean;
  variant?: 'default' | 'gold' | 'green' | 'cream';
  icon?: 'lock' | 'shield';
  height?: number;
}>();

// Directional glyph rendered outside the translated label so it can mirror under RTL.
const CTA_ARROW = '→';
const ctaLabel = computed(() => cta ?? t('property.shell.signUpFreeArrow'));
const showCtaArrow = computed(() => ctaArrow || cta === undefined);

const emit = defineEmits<{
  (e: 'cta-click'): void;
  (e: 'cta2-click'): void;
}>();

const variantClasses = computed(() => {
  switch (variant) {
    case 'gold':
      return 'bg-gold-primary-10 border-gold-primary-100/45';
    case 'green':
      return 'bg-success/10 border-success/35';
    case 'cream':
      return 'bg-[#FAF6EC] border-black/20';
    default:
      return 'bg-[#FAF6EC] border-black/20';
  }
});

const iconColorClass = computed(() => (variant === 'green' ? 'text-success' : 'text-black-60'));

const wrapperStyle = computed(() => (height ? { minHeight: `${height}px` } : undefined));

const handleCtaClick = (event: Event) => {
  if (!ctaHref) {
    event.preventDefault();
  }
  emit('cta-click');
};

const handleCta2Click = (event: Event) => {
  event.preventDefault();
  emit('cta2-click');
};
</script>

<template>
  <div
    class="flex flex-col gap-2 rounded-2xl border-[1.5px] border-dashed p-6"
    :class="variantClasses"
    :style="wrapperStyle"
  >
    <div
      class="mb-1 inline-flex h-9 w-9 items-center justify-center rounded-[10px] bg-white"
      :class="iconColorClass"
      aria-hidden="true"
    >
      <svg
        v-if="icon === 'shield'"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 3l8 3v6c0 5-3.4 8.5-8 9.5C7.4 20.5 4 17 4 12V6l8-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
      <svg
        v-else
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect
          x="4"
          y="11"
          width="16"
          height="10"
          rx="2"
        />
        <path d="M8 11V8a4 4 0 1 1 8 0v3" />
      </svg>
    </div>
    <div class="font-serif text-[18px] font-semibold leading-[1.2] tracking-[-0.015em] text-black-100">
      {{ title }}
    </div>
    <p class="m-0 text-[13px] leading-[1.5] text-black-70">
      {{ message }}
    </p>
    <div class="mt-1.5 flex flex-wrap gap-2">
      <a
        :href="ctaHref ?? '#'"
        class="inline-flex h-10 items-center justify-center rounded-[10px] bg-black-100 px-4 text-[13px] font-bold text-white transition-colors hover:bg-black-90"
        @click="handleCtaClick"
      >
        <span
          >{{ ctaLabel }}
          <span
            v-if="showCtaArrow"
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >{{ CTA_ARROW }}</span
          ></span
        >
      </a>
      <a
        v-if="cta2"
        href="#"
        class="inline-flex h-10 items-center justify-center rounded-[10px] border border-black/[0.15] bg-white px-4 text-[13px] font-semibold text-black-100 transition-colors hover:bg-black-03"
        @click="handleCta2Click"
      >
        {{ cta2 }}
      </a>
    </div>
  </div>
</template>
